import * as React2 from 'react';
import { useCatalogAdvanced, usePrimaryWriteTarget, usePrimaryChains, useTierCutoffs, Dialog, DialogTrigger, Button, DialogContent, DialogHeader, DialogTitle, DialogDescription, ActorName, AuditTimestamp, CatalogItemForm, WhyPanel, CatalogFieldOverridesEditor, DialogFooter, useOfferedRungs, Tabs, TabsList, TabsTrigger, catalogWriteErrorMessage, Input, ColorPicker, Textarea, LogoUpload, Label, StarIcon, cn, DropdownMenu, DropdownMenuTrigger, ChevronDownIcon, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem, useLadderShared, ResourceTable, useEntitlement, useReadConfidence, useDebounced, EnumSelect, ChainSelect, notifySuccess, ErrorState, AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, withPrimaryChainsAllowed, basicCatalogDraft, AddressDisplay, Badge, PrimaryChainBadge, ChainAvatar, CustomizeIcon, LockIcon, ShareUpIcon, PencilIcon, TrashIcon, useSignedCdnImage, ColorSwatch, ReadConfidenceBadge, CopyIcon } from '@authe/ui-core';
export { useAction } from '@authe/ui-core';
import { CATALOG_LIFECYCLES, CATALOG_LIFECYCLE_LABEL, draftToRowBody, catalogResource, samePrimaryChain, resolvePrimaryChoices, EMPTY_PRIMARY_STATE, CATALOG_PRIMARY_RESOURCE, primaryChoiceAt, primarySetBy, primaryHiddenBy, rowToDraft, emptyDraft, overridesDraftFromDto, CATALOG_ITEM_KIND_LABEL, presentationOverrideOf, deriveCatalogOwnership, CATALOG_ITEM_KINDS, duplicateDraft, retirementImpact, PRIMARY_KINDS, PRIMARY_RUNG_OWNER, primaryChoiceFromWire, primaryStateFromWire, validateDraft, diffFieldOverrides, isEmptyFieldOverrides, overridesDraftToDto, primaryAppliesTo, customisationReach } from '@authe/fe-commons/catalog';
export { CATALOG_ITEM_KINDS, CATALOG_ITEM_KIND_LABEL, CatalogItemKind, deriveCatalogOwnership, draftToRowBody, emptyDraft, isChainAgnostic, rowToDraft, validateDraft } from '@authe/fe-commons/catalog';
import { withArticle, asString, pluralize } from '@authe/fe-commons/display';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { toStoredChainId, toChainSlug } from '@authe/fe-commons/chains';
import { useOptionalResourceChanges, useAsyncResource, useResourceInvalidation } from '@authe/ui-core/api';
export { useAsyncResource } from '@authe/ui-core/api';
import { isVisibilityPolicyRefusal, scoredConfidence } from '@authe/fe-commons/attribution';
import { toErrorResponse } from '@authe/fe-commons/errors';
import { sagaKindsByShape, sagaLabel, groupShapeLabel, groupShapeMeaning, groupStateLabel, groupStateMeaning } from '@authe/fe-commons/grouping';
import { ENTITLEMENT } from '@authe/fe-commons/plans';
import { withResourceMutations } from '@authe/fe-commons/events';
import { unwrapData } from '@authe/fe-commons/api';
import { uploadLogo, assetUrl } from '@authe/fe-commons/assets';
import { randomHex } from '@authe/fe-commons/ids';
import { createLogger } from '@authe/fe-commons/logging';

// src/CatalogManager.tsx
var CatalogContext = React2.createContext(null);
function CatalogProvider({
  api,
  defaultQuery,
  subscribe,
  children
}) {
  const value = React2.useMemo(
    () => ({ api, defaultQuery, subscribe }),
    [api, defaultQuery, subscribe]
  );
  return /* @__PURE__ */ jsx(CatalogContext.Provider, { value, children });
}
function useCatalog() {
  const value = React2.useContext(CatalogContext);
  if (!value) throw new Error("useCatalog must be used within a <CatalogProvider>");
  return value;
}
function useOptionalCatalog() {
  return React2.useContext(CatalogContext);
}

// src/data/liveCatalog.ts
function isNarrowedQuery(query) {
  if (!query) return false;
  return Boolean(query.minConfidence !== void 0 || query.includeUnscored !== void 0 || query.confidenceReview || query.search || query.chain || query.lifecycle || query.groupType || query.sagaKind);
}
function mergeStreamedCatalogRow(loaded, streamed) {
  return { ...loaded, ...streamed };
}
function applyCatalogChange(rows, change) {
  if (!rows || rows.length === 0) return rows;
  const index = rows.findIndex((row) => row.id === change.id);
  if (index === -1) return rows;
  if (change.event === "deleted") {
    return rows.filter((row) => row.id !== change.id);
  }
  if (!change.row) return rows;
  const next = [...rows];
  next[index] = mergeStreamedCatalogRow(rows[index], change.row);
  return next;
}
function catalogChangeForRow(id, change) {
  if (!id || change.id !== id) return null;
  if (change.event === "deleted") return { gone: true };
  return { gone: false, patch: change.row };
}

// src/data/catalogPaging.ts
function nextCursor(first, tail) {
  return tail ? tail.cursor : first?.nextCursor;
}
function hasMore(first, tail) {
  return Boolean(nextCursor(first, tail)) && (tail ? tail.hasMore : Boolean(first?.hasMore));
}
function allRows(first, tail) {
  return [...first?.items ?? [], ...tail?.rows ?? []];
}
function appendPage(prev, page) {
  return {
    rows: [...prev?.rows ?? [], ...page.items],
    cursor: page.nextCursor,
    hasMore: Boolean(page.hasMore)
  };
}
function upsertRow(rows, next) {
  const list = rows ?? [];
  return list.some((r) => r.id === next.id) ? list.map((r) => r.id === next.id ? next : r) : [next, ...list];
}
function tailHolds(tail, id) {
  return (tail?.rows ?? []).some((r) => r.id === id);
}

// src/data/useCatalogItems.ts
var EMPTY_IDS = /* @__PURE__ */ new Set();
var AUTO_PAGE_LIMIT = 3;
function shiftTotal(total, by, floor) {
  if (typeof total !== "number") return total;
  return Math.max(floor, total + by);
}
function useCatalogItems(kind, query) {
  const { api, defaultQuery, subscribe } = useCatalog();
  const advanced = useCatalogAdvanced();
  const mergedQuery = React2.useMemo(() => ({ ...defaultQuery, ...query }), [defaultQuery, query]);
  const queryKey = JSON.stringify(mergedQuery);
  const changes = useOptionalResourceChanges();
  const resource = useAsyncResource(() => api.list(kind, mergedQuery), [kind, queryKey, changes?.scope]);
  const [tail, setTail] = React2.useState(null);
  const [isLoadingMore, setIsLoadingMore] = React2.useState(false);
  const [loadMoreError, setLoadMoreError] = React2.useState(null);
  const readKey = `${changes?.scope ?? ""}|${kind}|${queryKey}`;
  const pageGeneration = React2.useRef(0);
  const autoPages = React2.useRef(0);
  const [pending, setPending] = React2.useState(EMPTY_IDS);
  const [lastRead, setLastRead] = React2.useState(readKey);
  if (lastRead !== readKey) {
    setLastRead(readKey);
    setTail(null);
    setIsLoadingMore(false);
    setLoadMoreError(null);
    autoPages.current = 0;
    if (pending !== EMPTY_IDS) setPending(EMPTY_IDS);
  }
  React2.useLayoutEffect(() => {
    pageGeneration.current += 1;
    return () => {
      pageGeneration.current += 1;
    };
  }, [readKey]);
  const first = resource.data;
  const cursor = nextCursor(first, tail);
  const more = hasMore(first, tail);
  const loadMore = React2.useCallback(() => {
    if (!cursor || isLoadingMore) return;
    setIsLoadingMore(true);
    setLoadMoreError(null);
    const generation = pageGeneration.current;
    void api.list(kind, { ...mergedQuery, cursor }).then((page) => {
      if (generation === pageGeneration.current) setTail((prev) => appendPage(prev, page));
    }).catch((err) => {
      if (generation === pageGeneration.current) setLoadMoreError(err);
    }).finally(() => {
      if (generation === pageGeneration.current) setIsLoadingMore(false);
    });
  }, [api, cursor, isLoadingMore, kind, mergedQuery]);
  const patchTail = React2.useCallback(
    (fn) => setTail((prev) => prev ? { ...prev, rows: fn(prev.rows) } : prev),
    []
  );
  const reconcileChains = React2.useCallback(
    async (id, chains) => {
      if (api.setChains && chains !== void 0)
        await api.setChains(kind, id, chains.map((c) => toStoredChainId(c) ?? c));
    },
    [api, kind]
  );
  const create = React2.useCallback(
    async (draft, options) => {
      const body = draftToRowBody(draft);
      const rung = options?.rung && options.rung !== "tenant" ? options.rung : void 0;
      const row = await api.create(kind, rung ? { ...body, rung } : body);
      const appliesTo = kind === "address" ? draft.chains : advanced ? draft.chains ?? [] : draft.chains;
      await reconcileChains(row.id, appliesTo);
      const withChains = { ...row, chains: draft.chains ?? row.chains };
      if (resource.data?.confidencePolicy?.active || isNarrowedQuery(mergedQuery)) {
        pageGeneration.current += 1;
        setTail(null);
        setIsLoadingMore(false);
        resource.refetch();
      } else
        resource.setData((prev) => {
          const items2 = upsertRow(prev?.items, withChains);
          return { ...prev, items: items2, total: shiftTotal(prev?.total, 1, items2.length) };
        });
      return withChains;
    },
    [advanced, api, kind, mergedQuery, reconcileChains, resource]
  );
  const update = React2.useCallback(
    async (id, draft) => {
      const row = await api.update(kind, id, draftToRowBody(draft));
      await reconcileChains(id, draft.chains);
      const withChains = { ...row, chains: draft.chains ?? row.chains };
      if (resource.data?.confidencePolicy?.active || isNarrowedQuery(mergedQuery)) {
        pageGeneration.current += 1;
        setIsLoadingMore(false);
        setTail(null);
        resource.refetch();
      } else if (tailHolds(tail, id)) patchTail((rows) => rows.map((r) => r.id === id ? withChains : r));
      else resource.setData((prev) => ({ ...prev, items: upsertRow(prev?.items, withChains) }));
      return withChains;
    },
    [api, kind, mergedQuery, patchTail, reconcileChains, resource, tail]
  );
  const remove = React2.useCallback(
    async (id) => {
      await api.remove(kind, id);
      resource.setData((prev) => {
        const items2 = (prev?.items ?? []).filter((r) => r.id !== id);
        const removed = items2.length !== (prev?.items ?? []).length;
        return { ...prev, items: items2, total: removed ? shiftTotal(prev?.total, -1, items2.length) : prev?.total };
      });
      patchTail((rows) => rows.filter((r) => r.id !== id));
    },
    [api, kind, patchTail, resource]
  );
  const saveChains = React2.useCallback(
    async (id, chains) => {
      await reconcileChains(id, chains);
      const withChains = (r) => r.id === id ? { ...r, chains } : r;
      resource.setData((prev) => ({ ...prev, items: (prev?.items ?? []).map(withChains) }));
      patchTail((rows) => rows.map(withChains));
    },
    [patchTail, reconcileChains, resource]
  );
  const replaceRow = React2.useCallback(
    (row) => {
      const swap = (r) => r.id === row.id ? { ...row, chains: row.chains ?? r.chains } : r;
      resource.setData((prev) => prev ? { ...prev, items: (prev.items ?? []).map(swap) } : prev);
      patchTail((rows) => rows.map(swap));
    },
    [patchTail, resource]
  );
  const promote = React2.useCallback(
    async (id, toRung, onStep) => {
      if (!api.promote) throw new Error("Moving a row is not available here");
      let cursor2;
      for (let steps = 0; steps < 500; steps += 1) {
        const step = await api.promote(kind, id, toRung, cursor2);
        onStep?.(step);
        if (step.done) {
          const moved = step.row;
          const place = (rows) => {
            const rest = (rows ?? []).filter((r) => r.id !== step.targetId);
            return moved ? rest.map((r) => r.id === id ? moved : r) : rest.filter((r) => r.id !== id);
          };
          resource.setData((prev) => prev ? { ...prev, items: place(prev.items) } : prev);
          patchTail((rows) => place(rows));
          return step;
        }
        cursor2 = step.cursor;
      }
      throw new Error("The move did not finish; run it again to carry on");
    },
    [api, kind, patchTail, resource]
  );
  const items = React2.useMemo(() => allRows(first, tail), [first, tail]);
  React2.useEffect(() => {
    if (resource.isLoading || isLoadingMore || loadMoreError) return;
    if (items.length > 0 || !more) {
      autoPages.current = 0;
      return;
    }
    if (autoPages.current >= AUTO_PAGE_LIMIT) return;
    autoPages.current += 1;
    loadMore();
  }, [items.length, more, resource.isLoading, isLoadingMore, loadMoreError, loadMore]);
  const narrowed = isNarrowedQuery(mergedQuery) || Boolean(first?.confidencePolicy?.active);
  const narrowedRef = React2.useRef(narrowed);
  const itemsRef = React2.useRef(items);
  const resourceRef = React2.useRef(resource);
  React2.useEffect(() => {
    narrowedRef.current = narrowed;
    itemsRef.current = items;
    resourceRef.current = resource;
  });
  React2.useEffect(() => {
    if (!subscribe) return;
    return subscribe(kind, (change) => {
      if (!resourceRef.current.data) return;
      if (resourceRef.current.data.confidencePolicy?.active) {
        pageGeneration.current += 1;
        setIsLoadingMore(false);
        resourceRef.current.refetch();
        setTail(null);
        return;
      }
      if (itemsRef.current.some((row) => row.id === change.id)) {
        resourceRef.current.setData((prev) => {
          if (!prev) return prev;
          const next = applyCatalogChange(prev.items, change);
          return next === prev.items ? prev : { ...prev, items: next ?? [] };
        });
        setTail((prev) => {
          if (!prev) return prev;
          const next = applyCatalogChange(prev.rows, change);
          return next === prev.rows ? prev : { ...prev, rows: next ?? [] };
        });
        return;
      }
      if (change.event !== "created") return;
      if (change.row && !narrowedRef.current) {
        const row = change.row;
        resourceRef.current.setData((prev) => ({ ...prev, items: upsertRow(prev?.items, row) }));
        return;
      }
      setPending((prev) => prev.has(change.id) ? prev : new Set(prev).add(change.id));
    });
  }, [subscribe, kind]);
  const refetch = React2.useCallback(() => {
    pageGeneration.current += 1;
    setTail(null);
    setIsLoadingMore(false);
    setLoadMoreError(null);
    setPending(EMPTY_IDS);
    resourceRef.current.refetch();
  }, []);
  useResourceInvalidation(catalogResource(kind), refetch);
  return {
    items,
    isLoading: resource.isLoading,
    error: resource.error,
    refetch,
    create,
    update,
    remove,
    saveChains,
    replaceRow,
    promote,
    authorableRungs: first?.authorableRungs ?? ["tenant"],
    hasMore: more,
    loadMore,
    isLoadingMore,
    loadMoreError,
    total: first?.total,
    totalCapped: first?.totalCapped,
    confidencePolicy: first?.confidencePolicy,
    pendingCount: pending.size
  };
}
var useLabels = (query) => useCatalogItems("label", query);
var useTags = (query) => useCatalogItems("tag", query);
var useEntities = (query) => useCatalogItems("entity", query);
var useGroups = (query) => useCatalogItems("group", query);
var useAddresses = (query) => useCatalogItems("address", query);
var EMPTY_PRIMARIES = Object.freeze({});
var targetKey = (t) => `${t.scope ?? "address"}:${t.targetId}:${t.chainId ?? "_"}`;
function withPrimaryChoice(state, kind, rung, chainId, next) {
  const choices = state.choices.filter(
    (c) => !(c.kind === kind && c.rung === rung && samePrimaryChain(c.chainId, chainId))
  );
  if (next) choices.push(next);
  const resolved = resolvePrimaryChoices(
    choices.filter((c) => c.kind === kind),
    chainId
  );
  const primaries = { ...state.primaries };
  delete primaries[kind];
  const winner = resolved.primaries[kind];
  if (winner) primaries[kind] = winner;
  const hidden = state.hidden.filter((k) => k !== kind);
  if (resolved.hidden.includes(kind)) hidden.push(kind);
  return { primaries, hidden, choices };
}
function describePrimaryFailure(error) {
  if (error == null) return null;
  if (isVisibilityPolicyRefusal(error)) {
    return {
      title: "Your organization's policy doesn't allow this",
      detail: "The visibility policy doesn't allow a primary to be set where your defaults put it. Change who your primaries apply to in your Default visibility settings, or ask an administrator."
    };
  }
  if (toErrorResponse(error).statusCode === 409) {
    return {
      title: "Someone else changed this primary",
      detail: "It changed after it was loaded here. The current choice is shown now \u2014 try again if you still want yours."
    };
  }
  return null;
}
function usePrimaryAssociations(target) {
  const { api } = useCatalog();
  const key = targetKey(target);
  const changes = useOptionalResourceChanges();
  const enabled = typeof api.getPrimaries === "function";
  const { rung: writeRung } = usePrimaryWriteTarget();
  const chainId = target.chainId ?? null;
  const resource = useAsyncResource(
    () => api.getPrimaries ? api.getPrimaries(target) : Promise.resolve(EMPTY_PRIMARY_STATE),
    [key, enabled, changes?.scope]
  );
  useResourceInvalidation(CATALOG_PRIMARY_RESOURCE, resource.refetch);
  const data = resource.data;
  const state = data && typeof data.primaries === "object" && Array.isArray(data.hidden) && Array.isArray(data.choices) ? data : EMPTY_PRIMARY_STATE;
  const primaries = React2.useMemo(() => {
    const out = {};
    let any = false;
    for (const [kind, choice] of Object.entries(state.primaries)) {
      if (kind === "address" || !choice?.catalogId) continue;
      out[kind] = choice.catalogId;
      any = true;
    }
    return any ? out : EMPTY_PRIMARIES;
  }, [state.primaries]);
  const [actionError, setActionError] = React2.useState(null);
  const dismissActionError = React2.useCallback(() => setActionError(null), []);
  const isPrimary = React2.useCallback(
    (kind, catalogId) => primaries[kind] === catalogId,
    [primaries]
  );
  const ownChoice = React2.useCallback(
    (kind) => primaryChoiceAt(state.choices, kind, writeRung, chainId),
    [state.choices, writeRung, chainId]
  );
  const provenance = React2.useCallback(
    (kind) => {
      const choice = state.primaries[kind];
      if (!choice) return void 0;
      return { rung: choice.rung, label: primarySetBy(choice.rung), own: choice.rung === writeRung };
    },
    [state.primaries, writeRung]
  );
  const hiddenBy = React2.useCallback(
    (kind) => primaryHiddenBy(state, kind, chainId),
    [state, chainId]
  );
  const run = React2.useCallback(
    async (write, apply) => {
      setActionError(null);
      let stored;
      try {
        stored = await write();
      } catch (err) {
        setActionError(err);
        if (toErrorResponse(err).statusCode === 409) resource.refetch();
        return;
      }
      resource.setData(apply(stored));
    },
    [resource]
  );
  const choose = React2.useCallback(
    async (kind, catalogId, options = {}) => {
      if (!api.setPrimary) return;
      const rung = options.rung ?? writeRung;
      const current = primaryChoiceAt(state.choices, kind, rung, chainId);
      const writeOptions = {
        rung,
        ...current?.version != null ? { expectedVersion: current.version } : {}
      };
      await run(
        () => api.setPrimary(target, kind, catalogId, writeOptions),
        (stored) => (prev) => withPrimaryChoice(prev ?? EMPTY_PRIMARY_STATE, kind, rung, chainId, {
          kind,
          catalogId,
          rung,
          chainId,
          version: (current?.version ?? -1) + 1,
          setBy: null,
          setAt: null,
          // What the server stored, where it said: its version is the next guard.
          ...stored && stored.kind === kind ? stored : {}
        })
      );
    },
    [api, chainId, run, state.choices, target, writeRung]
  );
  const setPrimary = React2.useCallback(
    (kind, catalogId, options) => choose(kind, catalogId, options),
    [choose]
  );
  const hidePrimary = React2.useCallback(
    (kind, options) => choose(kind, null, options),
    [choose]
  );
  const clearPrimary = React2.useCallback(
    async (kind, options = {}) => {
      if (!api.clearPrimary) return;
      const rung = options.rung ?? writeRung;
      await run(
        () => api.clearPrimary(target, kind, { rung }),
        () => (prev) => {
          const base = prev ?? EMPTY_PRIMARY_STATE;
          if (base.choices.length === 0) {
            const primaries2 = { ...base.primaries };
            delete primaries2[kind];
            return { ...base, primaries: primaries2, hidden: base.hidden.filter((k) => k !== kind) };
          }
          return withPrimaryChoice(base, kind, rung, chainId, null);
        }
      );
    },
    [api, chainId, run, target, writeRung]
  );
  const togglePrimary = React2.useCallback(
    async (kind, catalogId) => {
      if (!isPrimary(kind, catalogId)) return setPrimary(kind, catalogId);
      const own = ownChoice(kind);
      if (own && own.catalogId === catalogId) return clearPrimary(kind);
      return hidePrimary(kind);
    },
    [clearPrimary, hidePrimary, isPrimary, ownChoice, setPrimary]
  );
  return {
    primaries,
    state,
    writeRung,
    provenance,
    hiddenBy,
    ownChoice,
    isLoading: resource.isLoading,
    error: resource.error,
    actionError,
    dismissActionError,
    refetch: resource.refetch,
    enabled,
    isPrimary,
    setPrimary,
    hidePrimary,
    clearPrimary,
    togglePrimary
  };
}
var RUNG_CHOICE = {
  tenant: { label: "This workspace", hint: "Only this workspace sees it." },
  org: { label: "Whole org", hint: "Every workspace in your org sees it and can attach it." },
  customer: { label: "Whole customer", hint: "Every workspace of your customer sees it and can attach it." }
};
function CatalogItemDialog({
  kind,
  mode = "create",
  row,
  initialDraft,
  rowUnavailable = false,
  onSubmit,
  trigger,
  open,
  onOpenChange,
  includeTestnets,
  title,
  description,
  canSetLifecycle = false,
  rungOptions
}) {
  const { api } = useCatalog();
  const [rung, setRung] = React2.useState("tenant");
  const choosableRungs = mode === "create" && rungOptions && rungOptions.length > 1 ? rungOptions : null;
  const advanced = useCatalogAdvanced();
  const primaryChains = usePrimaryChains();
  const tierCutoffs = useTierCutoffs();
  const [internalOpen, setInternalOpen] = React2.useState(false);
  const isControlled = open !== void 0;
  const isOpen = isControlled ? open : internalOpen;
  React2.useEffect(() => {
    if (isOpen) setRung("tenant");
  }, [isOpen]);
  const setOpen = React2.useCallback(
    (next) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );
  const [draft, setDraft] = React2.useState(
    () => mode === "edit" && row ? rowToDraft(kind, row) : initialDraft ?? emptyDraft(kind)
  );
  const [errors, setErrors] = React2.useState({});
  const [busy, setBusy] = React2.useState(false);
  const [submitError, setSubmitError] = React2.useState(null);
  const rowVersion = row ? JSON.stringify(rowToDraft(kind, row)) : null;
  const [baseVersion, setBaseVersion] = React2.useState(rowVersion);
  const [reloadVersion, setReloadVersion] = React2.useState(0);
  const conflicted = mode === "edit" && baseVersion !== rowVersion;
  const supportsOverrides = advanced && kind !== "address" && Boolean(api.getFieldOverrides || api.setFieldOverrides);
  const [overrides, setOverrides] = React2.useState({});
  const [overridesError, setOverridesError] = React2.useState(null);
  const [chainsError, setChainsError] = React2.useState(null);
  const initialOverrides = React2.useRef({});
  const toCandidate = React2.useCallback(
    (r) => ({
      id: r.id,
      name: typeof r.name === "string" && r.name ? r.name : r.id,
      color: typeof r.color === "string" ? r.color : void 0,
      logo: typeof r.icon === "string" ? r.icon : typeof r.logo_s3_uri === "string" ? r.logo_s3_uri : void 0
    }),
    []
  );
  const loadGroupCandidates = React2.useCallback(
    async (memberKind, search) => {
      const page = await api.list(memberKind, { search, limit: 20 });
      return page.items.map(toCandidate);
    },
    [api, toCandidate]
  );
  const getByIds = api.getByIds;
  const resolveGroupSelected = React2.useMemo(
    () => getByIds ? async (memberKind, ids) => (await getByIds(memberKind, ids)).map(toCandidate) : void 0,
    [getByIds, toCandidate]
  );
  React2.useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSubmitError(null);
    setOverrides({});
    setOverridesError(null);
    setChainsError(null);
    setBaseVersion(rowVersion);
    initialOverrides.current = {};
    if (mode === "edit" && row) {
      const base = rowToDraft(kind, row);
      setDraft({
        ...base,
        chains: row.chains?.map((c) => toChainSlug(c) ?? c)
      });
      let alive = true;
      if (advanced && api.getChains) {
        api.getChains(kind, row.id).then(
          (chains) => alive && setDraft(
            (d) => ({ ...d, chains: chains.map((c) => toChainSlug(c) ?? c) })
          )
        ).catch((err) => {
          if (alive) setChainsError(err);
        });
      }
      if (supportsOverrides && api.getFieldOverrides) {
        api.getFieldOverrides(kind, row.id).then((map) => {
          if (!alive) return;
          const drafts = {};
          for (const [chain, dto] of Object.entries(map))
            drafts[toChainSlug(chain) ?? chain] = overridesDraftFromDto(dto);
          initialOverrides.current = drafts;
          setOverrides(drafts);
        }).catch((err) => {
          if (alive) setOverridesError(err);
        });
      }
      return () => {
        alive = false;
      };
    }
    setDraft(initialDraft ?? emptyDraft(kind));
    return void 0;
  }, [isOpen, mode, kind, row?.id, advanced, reloadVersion]);
  const onUploadLogo = api.uploadLogo ? async (file) => {
    const id = draft.kind === "address" ? draft.address.trim() || void 0 : row?.id;
    const result = await api.uploadLogo({ kind, id, chain: draftChain(draft), file });
    return { key: result.key, url: result.url };
  } : void 0;
  const submit = async (event) => {
    event.preventDefault();
    if (busy || conflicted || rowUnavailable) return;
    const result = validateDraft(draft);
    if (!result.success || !result.data) {
      setErrors(result.fieldErrors);
      return;
    }
    setErrors({});
    setSubmitError(null);
    setBusy(true);
    try {
      const body = withPrimaryChainsAllowed(
        basicCatalogDraft(
          chainsError != null ? { ...result.data, chains: void 0 } : result.data,
          advanced
        ),
        primaryChains
      );
      const saved = await onSubmit(body, { rung: choosableRungs?.includes(rung) ? rung : "tenant" });
      const id = saved?.id ?? row?.id;
      if (id && supportsOverrides && api.setFieldOverrides) {
        const changes = diffFieldOverrides(initialOverrides.current, overrides);
        await Promise.all(
          changes.map(
            (c) => api.setFieldOverrides(kind, id, toStoredChainId(c.chainId) ?? c.chainId, c.value)
          )
        );
      }
      setOpen(false);
    } catch (e) {
      setSubmitError(catalogWriteErrorMessage(e, "Could not save"));
    } finally {
      setBusy(false);
    }
  };
  const kindLabel = CATALOG_ITEM_KIND_LABEL[kind];
  const heading = title ?? `${mode === "edit" ? "Edit" : "New"} ${kindLabel.toLowerCase()}`;
  const overrideBase = {
    name: draft.name,
    description: draft.description,
    color: draft.color,
    logo: draft.logo,
    aliases: draft.aliases
  };
  return /* @__PURE__ */ jsxs(Dialog, { open: isOpen, onOpenChange: setOpen, children: [
    trigger !== void 0 ? /* @__PURE__ */ jsx(DialogTrigger, { asChild: true, children: trigger }) : !isControlled ? /* @__PURE__ */ jsx(DialogTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { type: "button", children: [
      "New ",
      kindLabel.toLowerCase()
    ] }) }) : null,
    /* @__PURE__ */ jsx(DialogContent, { className: "max-h-[85vh] overflow-y-auto sm:max-w-lg", children: /* @__PURE__ */ jsxs("form", { onSubmit: submit, children: [
      /* @__PURE__ */ jsxs(DialogHeader, { children: [
        /* @__PURE__ */ jsx(DialogTitle, { children: heading }),
        /* @__PURE__ */ jsx(DialogDescription, { children: description || (mode === "edit" ? `Update this ${kindLabel.toLowerCase()}'s details.` : `Add ${withArticle(kindLabel.toLowerCase())} to your catalog.`) })
      ] }),
      choosableRungs && /* @__PURE__ */ jsxs("fieldset", { className: "mt-3 space-y-1.5", children: [
        /* @__PURE__ */ jsxs("legend", { className: "text-xs font-medium", children: [
          "Who shares this ",
          kindLabel.toLowerCase()
        ] }),
        /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1.5", role: "radiogroup", children: choosableRungs.map((r) => /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            size: "sm",
            role: "radio",
            "aria-checked": rung === r,
            variant: rung === r ? "default" : "outline",
            onClick: () => setRung(r),
            children: RUNG_CHOICE[r].label
          },
          r
        )) }),
        /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: RUNG_CHOICE[rung].hint })
      ] }),
      mode === "edit" && row ? /* @__PURE__ */ jsxs("dl", { className: "mt-3 space-y-1 rounded-sm border p-3 text-xs", children: [
        /* @__PURE__ */ jsx("dt", { className: "font-medium", children: "Record ID" }),
        /* @__PURE__ */ jsx("dd", { className: "select-all break-all font-mono", children: row.id }),
        /* @__PURE__ */ jsx("dt", { className: "font-medium", children: "Created by" }),
        /* @__PURE__ */ jsx("dd", { className: "select-all break-all", children: /* @__PURE__ */ jsx(ActorName, { id: typeof row.created_by === "string" ? row.created_by : null }) }),
        /* @__PURE__ */ jsx("dt", { className: "font-medium", children: "Created at" }),
        /* @__PURE__ */ jsx("dd", { children: /* @__PURE__ */ jsx(AuditTimestamp, { value: typeof row.created_at === "number" ? row.created_at : null, unit: "seconds" }) }),
        /* @__PURE__ */ jsx("dt", { className: "font-medium", children: "Last edited by" }),
        /* @__PURE__ */ jsx("dd", { className: "select-all break-all", children: /* @__PURE__ */ jsx(ActorName, { id: typeof row.updated_by === "string" ? row.updated_by : null }) }),
        /* @__PURE__ */ jsx("dt", { className: "font-medium", children: "Last edited at" }),
        /* @__PURE__ */ jsx("dd", { children: /* @__PURE__ */ jsx(AuditTimestamp, { value: typeof row.updated_at === "number" ? row.updated_at : null, unit: "seconds" }) })
      ] }) : null,
      /* @__PURE__ */ jsxs("div", { className: "my-4", children: [
        rowUnavailable ? /* @__PURE__ */ jsx("p", { role: "alert", className: "mb-3 text-sm text-destructive", children: "This item is no longer available in the current catalog. Your draft is kept here, but it cannot be saved. Close the editor and refresh the list to check its status." }) : conflicted ? /* @__PURE__ */ jsxs("div", { role: "alert", className: "mb-3 space-y-2 rounded-sm border p-3 text-sm", children: [
          /* @__PURE__ */ jsx("p", { children: "This item changed while you were editing. Your draft is kept here. Load the latest version before saving; doing so will replace your unsaved changes." }),
          /* @__PURE__ */ jsx(
            Button,
            {
              type: "button",
              variant: "outline",
              size: "sm",
              disabled: busy,
              onClick: () => setReloadVersion((version) => version + 1),
              children: "Load latest version"
            }
          )
        ] }) : null,
        /* @__PURE__ */ jsx(
          CatalogItemForm,
          {
            value: draft,
            onChange: setDraft,
            errors,
            onUploadLogo,
            resolveLogoUrl: api.resolveLogoUrl,
            includeTestnets,
            loadGroupCandidates,
            resolveGroupSelected,
            disabled: busy,
            lockName: mode === "edit" && kind !== "address",
            canSetLifecycle,
            advanced,
            primaryChains
          }
        ),
        chainsError != null && /* @__PURE__ */ jsxs("p", { role: "alert", className: "mt-2 text-xs text-risk-critical", children: [
          "This ",
          kindLabel.toLowerCase(),
          "'s chain applicability could not be loaded, so the picker is showing the list's copy of it. Saving will leave the stored chains untouched \u2014 reopen this dialog to edit them."
        ] }),
        mode === "edit" && row && // Glass-box: on an existing row, show *why* it exists (persisted
        // `explanation` bullets + headline confidence + lifecycle, with a
        // read-time fallback from raw `evidence`).
        /* @__PURE__ */ jsx("div", { className: "mt-4 rounded-md border bg-muted/20 p-3", children: /* @__PURE__ */ jsx(
          WhyPanel,
          {
            confidence: scoredConfidence(row.confidence, row),
            confidenceNote: "No confidence was recorded for this item.",
            cutoffs: tierCutoffs,
            lifecycle: typeof row.lifecycle === "string" ? row.lifecycle : void 0,
            explanation: Array.isArray(row.explanation) ? row.explanation : void 0,
            evidence: Array.isArray(row.evidence) ? row.evidence : void 0
          }
        ) }),
        supportsOverrides && /* @__PURE__ */ jsxs("details", { className: "mt-4 rounded-md border p-3", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-sm font-medium", children: "Per-chain overrides" }),
          /* @__PURE__ */ jsxs("p", { className: "mb-3 mt-1 text-xs text-muted-foreground", children: [
            "Customise how this ",
            kindLabel.toLowerCase(),
            " appears on specific chains. Blank fields inherit the base values above."
          ] }),
          overridesError != null && /* @__PURE__ */ jsxs("p", { role: "alert", className: "mb-3 text-xs text-risk-critical", children: [
            "This ",
            kindLabel.toLowerCase(),
            "'s existing per-chain overrides could not be loaded, so none are shown. Anything you add here is still saved; anything already stored is left as it is."
          ] }),
          /* @__PURE__ */ jsx(
            CatalogFieldOverridesEditor,
            {
              value: overrides,
              onChange: setOverrides,
              base: overrideBase,
              includeTestnets,
              disabled: busy,
              resolveLogoUrl: api.resolveLogoUrl,
              onUploadLogo: api.uploadLogo ? async (chain, file) => {
                const r = await api.uploadLogo({ kind, id: row?.id, chain, file });
                return { key: r.key, url: r.url };
              } : void 0
            }
          )
        ] }),
        submitError && /* @__PURE__ */ jsx("p", { className: "mt-3 text-sm text-destructive", children: submitError })
      ] }),
      /* @__PURE__ */ jsxs(DialogFooter, { children: [
        /* @__PURE__ */ jsx(Button, { type: "button", variant: "ghost", onClick: () => setOpen(false), children: "Cancel" }),
        /* @__PURE__ */ jsx(Button, { type: "submit", disabled: busy || conflicted || rowUnavailable, children: mode === "edit" ? "Save changes" : `Create ${kindLabel.toLowerCase()}` })
      ] })
    ] }) })
  ] });
}
function draftChain(draft) {
  if (draft.kind === "address" || draft.kind === "label") return draft.chain || void 0;
  return void 0;
}
var asText = (v) => typeof v === "string" && v !== "" ? v : void 0;
var LAYER_COPY = {
  tenant: { tab: "This workspace", reach: "your workspace", save: "Save for this workspace" },
  org: { tab: "Whole org", reach: "every workspace in your org", save: "Save for the org" },
  customer: { tab: "Whole customer", reach: "every workspace of your customer", save: "Save for the customer" }
};
var OWNER_COPY = {
  global: "a platform default",
  customer: "shared by your customer",
  org: "shared by your org",
  tenant: "this workspace's own"
};
function seedDraft(layer) {
  const seeded = overridesDraftFromDto(layer?.override ?? null);
  const logo = seeded.logo ?? seeded.icon;
  return { ...seeded, logo, icon: void 0 };
}
function CatalogPresentationDialog({
  kind,
  row,
  open,
  onOpenChange,
  onSaved
}) {
  const { api } = useCatalog();
  const offeredRungs = useOfferedRungs();
  const kindLabel = CATALOG_ITEM_KIND_LABEL[kind].toLowerCase();
  const [loaded, setLoaded] = React2.useState(null);
  const [loadError, setLoadError] = React2.useState(null);
  const [rung, setRung] = React2.useState("tenant");
  const [drafts, setDrafts] = React2.useState({});
  const [busy, setBusy] = React2.useState(false);
  const [submitError, setSubmitError] = React2.useState(null);
  React2.useEffect(() => {
    if (!open) return void 0;
    let alive = true;
    setLoaded(null);
    setLoadError(null);
    setSubmitError(null);
    setDrafts({});
    if (!api.getPresentation) return void 0;
    api.getPresentation(kind, row.id).then((result) => {
      if (!alive) return;
      setLoaded(result);
      setDrafts(Object.fromEntries(result.layers.map((layer2) => [layer2.rung, seedDraft(layer2)])));
      const editable = result.layers.filter((layer2) => layer2.editable).map((layer2) => layer2.rung);
      setRung(editable[editable.length - 1] ?? "tenant");
    }).catch((err) => {
      if (alive) setLoadError(err);
    });
    return () => {
      alive = false;
    };
  }, [open, kind, row.id]);
  const offered = (layer2) => layer2.rung === "tenant" || offeredRungs == null || offeredRungs.has(layer2.rung);
  const tabs = (loaded?.layers ?? []).filter((layer2) => layer2.editable && offered(layer2));
  const layer = loaded?.layers.find((l) => l.rung === rung);
  const inherited = layer?.inherited ?? loaded?.base ?? {};
  const draft = drafts[rung] ?? {};
  const customised = layer?.override != null;
  const patch = (next) => setDrafts((all) => ({ ...all, [rung]: { ...all[rung] ?? {}, ...next } }));
  const logo = draft.logo;
  const logoPreview = logo ? { key: logo, url: api.resolveLogoUrl?.(logo) } : null;
  const copy = LAYER_COPY[rung];
  const wider = (loaded?.layers ?? []).filter(
    (l) => l.override != null && l.rung !== rung && !tabs.includes(l)
  );
  const save = async (value) => {
    if (!api.setPresentation) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const dto = value == null || isEmptyFieldOverrides(value) ? null : overridesDraftToDto(value);
      if (dto == null && !customised) {
        onOpenChange(false);
        return;
      }
      const result = await api.setPresentation(kind, row.id, rung, dto);
      onSaved?.(result);
      onOpenChange(false);
    } catch (err) {
      setSubmitError(
        catalogWriteErrorMessage(err, value == null ? "Could not reset this customisation" : "Could not save the customisation")
      );
    } finally {
      setBusy(false);
    }
  };
  const aliasesText = (draft.aliases ?? []).join(", ");
  const disabled = busy || loaded == null || !tabs.some((t) => t.rung === rung);
  const baseName = asText(loaded?.base.name) ?? asText(presentationOverrideOf(row)?.base.name) ?? asText(row.name) ?? row.id;
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsx(DialogContent, { className: "max-h-[85vh] overflow-y-auto sm:max-w-lg", children: /* @__PURE__ */ jsxs(
    "form",
    {
      onSubmit: (event) => {
        event.preventDefault();
        void save(draft);
      },
      children: [
        /* @__PURE__ */ jsxs(DialogHeader, { children: [
          /* @__PURE__ */ jsxs(DialogTitle, { children: [
            "Customise \u201C",
            baseName,
            "\u201D"
          ] }),
          /* @__PURE__ */ jsxs(DialogDescription, { children: [
            "This ",
            kindLabel,
            " is ",
            OWNER_COPY[loaded?.rowRung ?? "global"],
            ". Changes here only affect how it reads for ",
            copy.reach,
            "; the ",
            kindLabel,
            " itself and everything attached to it stay the same. Leave a field blank to keep the value shown."
          ] })
        ] }),
        tabs.length > 1 && /* @__PURE__ */ jsx(Tabs, { value: rung, onValueChange: (v) => setRung(v), className: "mt-3", children: /* @__PURE__ */ jsx(TabsList, { children: tabs.map((t) => /* @__PURE__ */ jsxs(TabsTrigger, { value: t.rung, children: [
          LAYER_COPY[t.rung].tab,
          t.override != null ? " \u2022" : ""
        ] }, t.rung)) }) }),
        /* @__PURE__ */ jsxs("div", { className: "my-4 space-y-3", children: [
          loadError != null ? /* @__PURE__ */ jsx("p", { role: "alert", className: "text-sm text-destructive", children: catalogWriteErrorMessage(loadError, "Could not load the customisation.") }) : loaded == null ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "Loading\u2026" }) : null,
          wider.map((l) => /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
            "Your ",
            l.rung === "org" ? "org" : l.rung === "customer" ? "customer" : "workspace",
            " customised this",
            " ",
            kindLabel,
            "; its values show as placeholders below."
          ] }, l.rung)),
          /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-3", children: [
            /* @__PURE__ */ jsx(OField, { label: "Name", inherited: asText(inherited.name), children: /* @__PURE__ */ jsx(
              Input,
              {
                value: draft.name ?? "",
                disabled,
                placeholder: asText(inherited.name) ?? "Name",
                onChange: (e) => patch({ name: e.target.value })
              }
            ) }),
            /* @__PURE__ */ jsx(OField, { label: "Colour", inherited: asText(inherited.color), children: /* @__PURE__ */ jsx(ColorPicker, { value: draft.color, disabled, onChange: (c) => patch({ color: c ?? void 0 }) }) })
          ] }),
          /* @__PURE__ */ jsx(OField, { label: "Description", inherited: asText(inherited.description), children: /* @__PURE__ */ jsx(
            Textarea,
            {
              rows: 2,
              value: draft.description ?? "",
              disabled,
              placeholder: asText(inherited.description) ?? "Description",
              onChange: (e) => patch({ description: e.target.value })
            }
          ) }),
          /* @__PURE__ */ jsx(OField, { label: "Aliases", hint: `Comma-separated. Replaces the aliases for ${copy.reach}.`, children: /* @__PURE__ */ jsx(
            Input,
            {
              value: aliasesText,
              disabled,
              placeholder: (inherited.aliases ?? []).join(", ") || "None",
              onChange: (e) => patch({
                aliases: e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
              })
            }
          ) }),
          api.uploadLogo && /* @__PURE__ */ jsx(OField, { label: "Logo", hint: `Shown to ${copy.reach}; everyone else keeps the logo above.`, children: /* @__PURE__ */ jsx(
            LogoUpload,
            {
              value: logoPreview,
              disabled,
              onUpload: async (file) => {
                const r = await api.uploadLogo({ kind, id: row.id, file });
                return { key: r.key, url: r.url };
              },
              onChange: (next) => patch({ logo: next?.key })
            }
          ) }),
          customised && layer?.updatedAt ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
            "Customised by ",
            /* @__PURE__ */ jsx(ActorName, { id: layer.updatedBy ?? null }),
            " ",
            /* @__PURE__ */ jsx(AuditTimestamp, { value: layer.updatedAt, unit: "seconds" })
          ] }) : null,
          submitError && /* @__PURE__ */ jsx("p", { role: "alert", className: "text-sm text-destructive", children: submitError })
        ] }),
        /* @__PURE__ */ jsxs(DialogFooter, { className: "flex-wrap gap-2 sm:justify-between", children: [
          customised ? /* @__PURE__ */ jsxs(Button, { type: "button", variant: "outline", disabled: busy, onClick: () => void save(null), children: [
            "Reset ",
            LAYER_COPY[rung].tab.toLowerCase()
          ] }) : /* @__PURE__ */ jsx("span", {}),
          /* @__PURE__ */ jsxs("div", { className: "ml-auto flex gap-2", children: [
            /* @__PURE__ */ jsx(Button, { type: "button", variant: "ghost", onClick: () => onOpenChange(false), children: "Cancel" }),
            /* @__PURE__ */ jsx(Button, { type: "submit", disabled, children: copy.save })
          ] })
        ] })
      ]
    }
  ) }) });
}
function OField({
  label,
  inherited,
  hint,
  children
}) {
  return /* @__PURE__ */ jsxs("div", { className: "space-y-1.5", children: [
    /* @__PURE__ */ jsx(Label, { className: "block text-xs", children: label }),
    children,
    inherited ? /* @__PURE__ */ jsxs("p", { className: "truncate text-xs text-muted-foreground", children: [
      "Otherwise: ",
      inherited
    ] }) : hint ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: hint }) : null
  ] });
}
var TARGET_COPY = {
  org: { label: "Whole org", reach: "every workspace in your org" },
  customer: { label: "Whole customer", reach: "every workspace of your customer" }
};
var WIDTH = { user: 0, tenant: 1, org: 2, customer: 3, global: 4 };
function CatalogPromoteDialog({
  kind,
  row,
  targets,
  open,
  onOpenChange,
  onPromote,
  onDone
}) {
  const kindLabel = CATALOG_ITEM_KIND_LABEL[kind].toLowerCase();
  const name = asString(row.name) ?? row.id;
  const from = deriveCatalogOwnership(row).scope;
  const choices = targets.filter((t) => (WIDTH[t] ?? 0) > (WIDTH[from] ?? 1));
  const [target, setTarget] = React2.useState(choices[0] ?? "org");
  const [busy, setBusy] = React2.useState(false);
  const [moved, setMoved] = React2.useState(0);
  const [error, setError] = React2.useState(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    setMoved(0);
    try {
      const result = await onPromote(
        row.id,
        target,
        (step) => setMoved((n) => n + (step.repointed.attachments ?? 0))
      );
      onDone?.(result);
      onOpenChange(false);
    } catch (err) {
      setError(catalogWriteErrorMessage(err, "The move stopped part way. Run it again to carry on."));
    } finally {
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange: (next) => !busy && onOpenChange(next), children: /* @__PURE__ */ jsxs(DialogContent, { className: "sm:max-w-lg", children: [
    /* @__PURE__ */ jsxs(DialogHeader, { children: [
      /* @__PURE__ */ jsxs(DialogTitle, { children: [
        "Share \u201C",
        name,
        "\u201D more widely"
      ] }),
      /* @__PURE__ */ jsxs(DialogDescription, { children: [
        "Moves this ",
        kindLabel,
        " from your workspace to ",
        TARGET_COPY[target].reach,
        ". Everything this workspace attached it to comes along, and links to the old one keep working. If",
        " ",
        withArticle(kindLabel),
        " with this name is already shared there, yours is merged into it and its details are kept."
      ] })
    ] }),
    choices.length > 1 && /* @__PURE__ */ jsx("div", { className: "mt-3 flex flex-wrap gap-1.5", role: "radiogroup", "aria-label": "Share with", children: choices.map((t) => /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        size: "sm",
        role: "radio",
        "aria-checked": target === t,
        variant: target === t ? "default" : "outline",
        disabled: busy,
        onClick: () => setTarget(t),
        children: TARGET_COPY[t].label
      },
      t
    )) }),
    /* @__PURE__ */ jsxs("div", { className: "my-3 space-y-2 text-sm", children: [
      busy && /* @__PURE__ */ jsxs("p", { role: "status", className: "text-muted-foreground", children: [
        "Moving\u2026 ",
        moved > 0 ? `${moved.toLocaleString()} attachments carried over so far.` : null
      ] }),
      error && /* @__PURE__ */ jsx("p", { role: "alert", className: "text-destructive", children: error })
    ] }),
    /* @__PURE__ */ jsxs(DialogFooter, { children: [
      /* @__PURE__ */ jsx(Button, { type: "button", variant: "ghost", disabled: busy, onClick: () => onOpenChange(false), children: "Cancel" }),
      /* @__PURE__ */ jsx(Button, { type: "button", disabled: busy || choices.length === 0, onClick: () => void run(), children: busy ? "Moving\u2026" : `Share with ${TARGET_COPY[target].label.toLowerCase()}` })
    ] })
  ] }) });
}
function PrimaryStar({
  active,
  onToggle,
  setBy,
  appliesTo,
  onHide,
  onUseInherited,
  disabled,
  label,
  size = "md",
  className
}) {
  const advanced = useCatalogAdvanced();
  const [busy, setBusy] = React2.useState(false);
  const guarded = React2.useCallback(
    async (action) => {
      if (busy || disabled) return;
      setBusy(true);
      try {
        await action();
      } catch {
      } finally {
        setBusy(false);
      }
    },
    [busy, disabled]
  );
  if (!advanced) return null;
  const dim = size === "sm" ? "size-4" : "size-5";
  const name = label ? ` ${label}` : "";
  const owner = active && setBy ? ` (${setBy})` : "";
  const audience = !active && appliesTo ? ` \u2014 ${appliesTo}` : "";
  const menu = active && (onHide || onUseInherited);
  const inert = disabled || busy;
  return /* @__PURE__ */ jsxs("span", { className: cn("inline-flex shrink-0 items-center", className), children: [
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        role: "switch",
        "aria-checked": active,
        "aria-label": active ? `Unset primary${name}${owner}` : `Set as primary${name}${audience}`,
        title: active ? `Primary${setBy ? ` \u2014 ${setBy}` : ""}` : `Set as primary${audience}`,
        onClick: () => void guarded(onToggle),
        disabled: inert,
        "data-primary": active ? "true" : "false",
        ...active && setBy ? { "data-primary-set-by": setBy } : {},
        className: cn(
          "inline-flex shrink-0 items-center justify-center rounded-md p-1.5 transition-colors",
          active ? "text-highlight hover:text-highlight/80" : "text-muted-foreground hover:bg-accent hover:text-foreground",
          inert && "pointer-events-none opacity-50"
        ),
        children: /* @__PURE__ */ jsx(StarIcon, { fill: active ? "currentColor" : "none", className: dim })
      }
    ),
    menu ? /* @__PURE__ */ jsxs(DropdownMenu, { children: [
      /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          "aria-label": `Primary options${name}`,
          disabled: inert,
          className: cn(
            "inline-flex h-6 w-4 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground",
            inert && "pointer-events-none opacity-50"
          ),
          children: /* @__PURE__ */ jsx(ChevronDownIcon, { className: "size-3" })
        }
      ) }),
      /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "start", children: [
        setBy ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(DropdownMenuLabel, { className: "text-xs font-normal text-muted-foreground", children: setBy }),
          /* @__PURE__ */ jsx(DropdownMenuSeparator, {})
        ] }) : null,
        onUseInherited ? /* @__PURE__ */ jsx(DropdownMenuItem, { onSelect: () => void guarded(onUseInherited), children: "Use inherited" }) : null,
        onHide ? /* @__PURE__ */ jsx(DropdownMenuItem, { onSelect: () => void guarded(onHide), children: "Hide primary" }) : null
      ] })
    ] }) : null
  ] });
}
var strArr = (v) => Array.isArray(v) ? v.filter((x) => typeof x === "string" && x !== "") : [];
var OWNERSHIP_BADGE = {
  user: "default",
  tenant: "secondary",
  org: "secondary",
  customer: "secondary",
  // The ladder's top rung, under its canonical name. The BADGE still reads
  // "Platform default" — the words are the reader's, the key is the ladder's.
  global: "outline"
};
function isPrimaryCapable(kind) {
  return PRIMARY_KINDS.includes(kind);
}
function isReadOnly(row) {
  return deriveCatalogOwnership(row).readOnly || row.group_type === "transaction";
}
function logoKeyOf(row) {
  return asString(row.icon) ?? asString(row.logo_s3_uri) ?? asString(row.logo);
}
function RowLogo({ logoUrl, color }) {
  const img = useSignedCdnImage(logoUrl);
  if (logoUrl && img.src) {
    return /* @__PURE__ */ jsx(
      "img",
      {
        src: img.src,
        ...img.crossOrigin ? { crossOrigin: img.crossOrigin } : {},
        alt: "",
        className: "size-6 shrink-0 rounded-md border object-cover",
        style: color ? { borderColor: color } : void 0
      }
    );
  }
  if (color) return /* @__PURE__ */ jsx(ColorSwatch, { color, size: "sm" });
  return /* @__PURE__ */ jsx("span", { className: "inline-block size-5 shrink-0 rounded-md border bg-muted" });
}
function RowWhy({ row, lifecycle }) {
  return /* @__PURE__ */ jsx(
    ReadConfidenceBadge,
    {
      score: scoredConfidence(row.confidence, row),
      lifecycle,
      explanation: Array.isArray(row.explanation) ? row.explanation : void 0,
      evidence: Array.isArray(row.evidence) ? row.evidence : void 0
    }
  );
}
function ownershipLabel(ownership, ladderChoosable) {
  return ladderChoosable ? ownership.label : ownership.soloLabel;
}
function createCatalogColumns({
  kind,
  onEdit,
  onDelete,
  onCustomize,
  onPromote,
  onDuplicate,
  resolveLogoUrl,
  showOwnership = true,
  ladderChoosable = true,
  primary
}) {
  const showPrimary = primary?.enabled === true && isPrimaryCapable(kind);
  const columns = [];
  if (showPrimary && isPrimaryCapable(kind)) {
    columns.push({
      id: "primary",
      header: "",
      // The star plus, on the lit one, its menu (whose choice / hide / inherit).
      size: 60,
      enableHiding: false,
      enableSorting: false,
      cell: ({ row }) => {
        const active = primary.isPrimary(kind, row.original.id);
        const setBy = active ? primary.provenance?.(kind)?.label : void 0;
        const own = active ? primary.ownChoice?.(kind) : void 0;
        const { hidePrimary, clearPrimary } = primary;
        return /* @__PURE__ */ jsx(
          PrimaryStar,
          {
            size: "sm",
            active,
            onToggle: () => primary.togglePrimary(kind, row.original.id),
            label: asString(row.original.name) ?? row.original.id,
            ...setBy ? { setBy } : {},
            ...primary.writeRung ? { appliesTo: primaryAppliesTo(primary.writeRung) } : {},
            ...active && hidePrimary ? { onHide: () => hidePrimary(kind) } : {},
            ...own && clearPrimary ? { onUseInherited: () => clearPrimary(kind) } : {}
          }
        );
      }
    });
  }
  columns.push({
    id: "name",
    // Sorted and searched on the NAME, not the whole identity cell: the badges
    // beside it are facts about the row, and a reader typing "verified" means
    // the lifecycle filter, not a name containing the word.
    accessorFn: (row) => asString(row.name) ?? row.id,
    header: "Name",
    enableHiding: false,
    // The only column here that was left to take whatever the others did not
    // want — which inverted the priority, because it is the one carrying the
    // identity AND a "Why?" chip AND (sometimes) a description, while "Status"
    // holds the word "candidate" and "Source" holds "Tenant". Measured on a
    // laptop-width window: Name 150px against Source 140 and Chains 180, so
    // "Binance", "Bithumb" and "Bitfinex" all rendered as `Bi…` on the surface
    // whose entire job is naming real-world parties.
    //
    // `minSize` is the floor that matters: it is what stops the identity column
    // being the FIRST to collapse as the window narrows.
    size: 360,
    minSize: 220,
    cell: ({ row }) => {
      const data = row.original;
      const name = asString(data.name) ?? data.id;
      const logoKey = logoKeyOf(data);
      const description = asString(data.description);
      const lifecycle = asString(data.lifecycle);
      const boundAddress = asString(data.address);
      const isTxGroup = data.group_type === "transaction";
      const sagaKind = asString(data.saga_kind);
      const legCount = typeof data.member_count === "number" ? data.member_count : void 0;
      const sagaState = asString(data.state);
      return /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-3", children: [
        /* @__PURE__ */ jsx(
          RowLogo,
          {
            logoUrl: resolveLogoUrl && logoKey ? resolveLogoUrl(logoKey) : void 0,
            color: asString(data.color)
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx("span", { className: "truncate text-sm font-medium", children: name }),
            boundAddress && /* @__PURE__ */ jsx(
              "span",
              {
                title: `Bound to ${boundAddress} \u2014 this ${kind} describes that address, and cannot be attached to a transaction.`,
                className: "shrink-0 rounded-sm border border-dashed px-1 py-px font-mono text-3xs text-muted-foreground",
                children: /* @__PURE__ */ jsx(AddressDisplay, { address: boundAddress })
              }
            ),
            isTxGroup && /* @__PURE__ */ jsx(
              Badge,
              {
                variant: "outline",
                className: "shrink-0 border-brand/40 text-3xs text-brand",
                title: groupShapeMeaning(sagaKind, asString(data.relationship_type)) + ` Found automatically by the grouping engine` + (sagaKind ? ` (${sagaKind})` : "") + `. Curated groups are the ones your team named.`,
                children: groupShapeLabel(sagaKind, asString(data.relationship_type))
              }
            ),
            isTxGroup && legCount != null && /* @__PURE__ */ jsxs("span", { className: "shrink-0 text-xs text-muted-foreground", children: [
              legCount,
              " ",
              legCount === 1 ? "leg" : "legs"
            ] }),
            isTxGroup && sagaState && /* @__PURE__ */ jsx(
              Badge,
              {
                variant: sagaState === "completed" ? "secondary" : "outline",
                className: "shrink-0 text-3xs",
                title: groupStateMeaning(sagaState),
                children: groupStateLabel(sagaState)
              }
            ),
            deriveCatalogOwnership(data).customized && /* @__PURE__ */ jsx(
              Badge,
              {
                variant: "outline",
                className: "shrink-0 border-brand/40 text-3xs text-brand",
                title: `Customised for ${customisationReach(data) ?? "your workspace"}${customisedFrom(data)}. Workspaces outside it see the original.`,
                children: "Customised"
              }
            ),
            /* @__PURE__ */ jsx(RowWhy, { row: data, lifecycle })
          ] }),
          description && /* @__PURE__ */ jsx("p", { className: "truncate text-xs text-muted-foreground", children: description })
        ] })
      ] });
    }
  });
  columns.push({
    id: "lifecycle",
    accessorFn: (row) => asString(row.lifecycle) ?? "",
    header: "Status",
    size: 120,
    cell: ({ row }) => {
      const lifecycle = asString(row.original.lifecycle);
      if (!lifecycle) return /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2014" });
      return /* @__PURE__ */ jsx(Badge, { variant: lifecycle === "verified" ? "default" : "secondary", className: "text-3xs", children: lifecycle });
    }
  });
  if (showOwnership) {
    columns.push({
      id: "ownership",
      // The label, not the scope token, because it is what the facet lists and
      // what the cell shows — a filter offering "platform" beside a cell reading
      // "Platform default" is two names for one thing.
      accessorFn: (row) => ownershipLabel(deriveCatalogOwnership(row), ladderChoosable),
      header: "Source",
      size: 140,
      cell: ({ row }) => {
        const ownership = deriveCatalogOwnership(row.original);
        return /* @__PURE__ */ jsx(Badge, { variant: OWNERSHIP_BADGE[ownership.scope], className: "text-3xs", children: ownershipLabel(ownership, ladderChoosable) });
      }
    });
  }
  columns.push({
    id: "chains",
    accessorFn: (row) => (row.chains ?? []).join(","),
    header: "Chains",
    size: 180,
    enableSorting: false,
    cell: ({ row }) => {
      const data = row.original;
      const chains = data.chains ?? [];
      const primaryChains = strArr(data.primary_chains);
      const primarySet = new Set(primaryChains);
      const shown = chains.length ? [...chains].sort((a, b) => Number(primarySet.has(b)) - Number(primarySet.has(a))) : primaryChains;
      return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
        primaryChains.length > 0 && /* @__PURE__ */ jsx(PrimaryChainBadge, { primary: true, className: "mr-0.5" }),
        chains.length === 0 && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "All chains" }),
        shown.slice(0, 4).map((c) => /* @__PURE__ */ jsx(
          ChainAvatar,
          {
            chain: c,
            size: "sm",
            showName: "sr-only",
            note: primarySet.has(c) ? "primary chain" : void 0,
            className: cn(primarySet.has(c) && "rounded-full ring-2 ring-highlight/70")
          },
          c
        )),
        shown.length > 4 && /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground", children: [
          "+",
          shown.length - 4
        ] })
      ] });
    }
  });
  columns.push({
    id: "actions",
    header: "",
    size: 120,
    enableHiding: false,
    enableSorting: false,
    cell: ({ row }) => {
      const data = row.original;
      const name = asString(data.name) ?? data.id;
      const isTxGroup = data.group_type === "transaction";
      const duplicate = onDuplicate && !isTxGroup ? /* @__PURE__ */ jsx(RowAction, { label: `Duplicate ${name}`, onClick: () => onDuplicate(data), children: /* @__PURE__ */ jsx(CopyIcon, { className: "size-4" }) }) : null;
      const ownership = deriveCatalogOwnership(data);
      const customize = onCustomize && !isTxGroup && ownership.scope !== "tenant" && ownership.scope !== "user" ? /* @__PURE__ */ jsx(
        RowAction,
        {
          label: `Customise ${name}`,
          title: `Customise how this ${OWNER_NOUN[ownership.scope]} row reads for your workspace`,
          onClick: () => onCustomize(data),
          children: /* @__PURE__ */ jsx(CustomizeIcon, { className: "size-4" })
        }
      ) : null;
      if (isReadOnly(data)) {
        return /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-0.5", children: [
          customize,
          duplicate,
          /* @__PURE__ */ jsx(
            "span",
            {
              className: "inline-flex items-center p-1.5 text-muted-foreground",
              title: isTxGroup ? "Detected automatically \u2014 maintained by the grouping engine, not editable here" : ownership.shared ? `Shared by your ${ownership.scope} \u2014 changing it for everyone needs the catalog promote permission` : "System default \u2014 the platform owns this row",
              "aria-label": isTxGroup ? `${name} is maintained by the grouping engine` : ownership.shared ? `${name} is shared by your ${ownership.scope}` : `${name} is a read-only system default`,
              children: /* @__PURE__ */ jsx(LockIcon, { className: "size-4" })
            }
          )
        ] });
      }
      return /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-0.5", children: [
        customize,
        onPromote && !isTxGroup && ownership.scope !== "customer" && /* @__PURE__ */ jsx(
          RowAction,
          {
            label: `Share ${name} with your ${ownership.scope === "org" ? "customer" : "org"}`,
            title: ownership.scope === "org" ? "Move this row to your customer, so every workspace of it shares it" : "Move this row to your org or customer, so every workspace there shares it",
            onClick: () => onPromote(data),
            children: /* @__PURE__ */ jsx(ShareUpIcon, { className: "size-4" })
          }
        ),
        duplicate,
        onEdit && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-label": `Edit ${name}`,
            onClick: (e) => {
              e.stopPropagation();
              onEdit(data);
            },
            className: "rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground",
            children: /* @__PURE__ */ jsx(PencilIcon, { className: "size-4" })
          }
        ),
        onDelete && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "aria-label": `Delete ${name}`,
            onClick: (e) => {
              e.stopPropagation();
              onDelete(data);
            },
            className: "rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive",
            children: /* @__PURE__ */ jsx(TrashIcon, { className: "size-4" })
          }
        )
      ] });
    }
  });
  return columns;
}
function RowAction({
  label,
  title,
  onClick,
  children
}) {
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      "aria-label": label,
      title: title ?? label,
      onClick: (e) => {
        e.stopPropagation();
        onClick();
      },
      className: "rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground",
      children
    }
  );
}
var OWNER_NOUN = {
  user: "personal",
  tenant: "workspace",
  org: "org",
  customer: "customer",
  global: "platform"
};
function customisedFrom(row) {
  const base = presentationOverrideOf(row)?.base.name;
  const name = asString(row.name);
  return base && name && base !== name ? ` (original name: ${base})` : "";
}
function countLabel(kind, loaded, total, capped) {
  const shown = total == null || total <= loaded ? loaded : total;
  const noun = pluralize(kind, shown);
  if (total == null || total <= loaded) return `${loaded.toLocaleString()} ${noun}`;
  return `${loaded.toLocaleString()} of ${total.toLocaleString()}${capped ? "+" : ""} ${noun}`;
}
function CatalogList({
  kind,
  items,
  isLoading,
  error,
  onRetry,
  onEdit,
  onDelete,
  onCustomize,
  onPromote,
  onDuplicate,
  onOpen,
  resolveLogoUrl,
  filtered = false,
  showOwnership = true,
  primary,
  emptyAction,
  hasMore: hasMore2 = false,
  onLoadMore,
  isLoadingMore = false,
  total,
  totalCapped,
  className
}) {
  const ladderChoosable = useLadderShared();
  const columns = React2.useMemo(
    () => createCatalogColumns({
      kind,
      onEdit,
      onDelete,
      onCustomize,
      onPromote,
      onDuplicate,
      resolveLogoUrl,
      showOwnership,
      ladderChoosable,
      primary
    }),
    [kind, onEdit, onDelete, onCustomize, onPromote, onDuplicate, resolveLogoUrl, showOwnership, ladderChoosable, primary]
  );
  return /* @__PURE__ */ jsxs("div", { className, children: [
    /* @__PURE__ */ jsx(
      ResourceTable,
      {
        rows: items,
        columns,
        getRowId: (row) => row.id,
        isLoading,
        error,
        onRetry,
        onRowClick: onOpen,
        toolbar: {
          // NO search box here, deliberately. The filter bar above this table
          // already owns searching, and its term goes UPSTREAM into the query.
          // A second box filtering the loaded rows by name is the exact failure
          // `ResourceTableToolbar.serverSearch` documents — it answers "not
          // here" about a paged list that does contain the row — and the two
          // boxes carried the SAME placeholder ("Search labels…"), one above the
          // other, with no way to tell which one you were typing into.
          columnLabels: {
            name: "Name",
            lifecycle: "Status",
            ownership: "Source",
            chains: "Chains"
          }
        },
        viewKey: `catalog:${kind}`,
        rowHeight: 56,
        height: "h-[560px]",
        emptyTitle: filtered ? `No ${pluralize(kind)} match` : `No ${pluralize(kind)} yet`,
        emptyDescription: filtered ? "Try a different term, widen the filters, or review all confidence scores." : `Create your first ${kind} to get started.`,
        emptyAction: filtered ? void 0 : emptyAction
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: isLoading ? "Loading catalog\u2026" : error ? "Catalog count unavailable" : countLabel(kind, items.length, total, totalCapped) }),
      hasMore2 && onLoadMore && /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          variant: "outline",
          size: "sm",
          onClick: onLoadMore,
          disabled: isLoadingMore,
          children: isLoadingMore ? "Loading\u2026" : "Load more"
        }
      )
    ] })
  ] });
}
var GROUP_TYPE_OPTIONS = [
  { value: "", label: "All groups" },
  { value: "transaction", label: "Auto-derived" },
  { value: "catalog", label: "Curated" }
];
var SAGA_KIND_OPTIONS = [
  { value: "", label: "Any kind" },
  ...sagaKindsByShape().flatMap(
    (shape) => shape.kinds.map((kind) => ({ value: kind, label: sagaLabel(kind), group: shape.title }))
  )
];
var LIFECYCLE_OPTIONS = [
  { value: "", label: "Any status" },
  ...CATALOG_LIFECYCLES.map((value) => ({ value, label: CATALOG_LIFECYCLE_LABEL[value] }))
];
function CatalogFilterBar({
  value,
  onChange,
  subject,
  showGroupFacets = false,
  includeTestnets,
  className
}) {
  const confidenceControls = useEntitlement(ENTITLEMENT.CATALOG_CONFIDENCE_CONTROLS);
  const { policy: inherited } = useReadConfidence();
  const inheritedPercent = Math.round(inherited.floor * 100);
  const [text, setText] = React2.useState(value.search ?? "");
  const settled = useDebounced(text.trim());
  const reported = React2.useRef(value.search ?? "");
  React2.useEffect(() => {
    if (settled === reported.current) return;
    reported.current = settled;
    onChange({ ...value, search: settled || void 0 });
  }, [settled]);
  const active = Boolean(value.minConfidence !== void 0 || value.includeUnscored !== void 0 || value.confidenceReview || value.search || value.chain || value.lifecycle || value.groupType || value.sagaKind);
  const originLocked = Boolean(value.sagaKind);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-wrap items-center gap-2", className), children: [
    /* @__PURE__ */ jsx(
      Input,
      {
        value: text,
        onChange: (e) => setText(e.target.value),
        placeholder: `Search ${subject}\u2026`,
        "aria-label": `Search ${subject}`,
        className: "h-8 w-full max-w-xs text-sm"
      }
    ),
    showGroupFacets && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        EnumSelect,
        {
          value: originLocked ? "transaction" : value.groupType ?? "",
          onChange: (groupType) => onChange({ ...value, groupType: groupType || void 0 }),
          options: GROUP_TYPE_OPTIONS,
          placeholder: "All groups",
          disabled: originLocked,
          "aria-label": "Filter groups by origin",
          title: originLocked ? "A saga kind only exists on auto-derived groups, so the kind filter implies this origin." : void 0,
          className: "h-8 w-[12rem] text-sm"
        }
      ),
      /* @__PURE__ */ jsx(
        EnumSelect,
        {
          value: value.sagaKind ?? "",
          onChange: (sagaKind) => onChange({ ...value, sagaKind: sagaKind || void 0 }),
          options: SAGA_KIND_OPTIONS,
          placeholder: "Any kind",
          "aria-label": "Filter groups by kind",
          className: "h-8 w-[14rem] text-sm",
          contentClassName: "max-h-[22rem]"
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      ChainSelect,
      {
        value: value.chain ?? "",
        onChange: (chain) => onChange({ ...value, chain: chain || void 0 }),
        allowAny: true,
        anyLabel: "Any chain",
        includeTestnets,
        className: "h-8 w-[11rem] text-sm"
      }
    ),
    /* @__PURE__ */ jsx(
      EnumSelect,
      {
        value: value.lifecycle ?? "",
        onChange: (lifecycle) => onChange({ ...value, lifecycle: lifecycle || void 0 }),
        options: LIFECYCLE_OPTIONS,
        placeholder: "Any status",
        className: "h-8 w-[10rem] text-sm"
      }
    ),
    confidenceControls && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-2 text-sm", children: [
        "Minimum confidence",
        /* @__PURE__ */ jsx(
          Input,
          {
            type: "number",
            min: 0,
            max: 100,
            step: 1,
            "aria-label": "Minimum catalog confidence percent",
            placeholder: inherited.floor > 0 ? `Inherited ${inheritedPercent}` : "Inherited 0",
            title: inherited.floor > 0 ? `Your confidence policy sets ${inheritedPercent}%. Type a number to override it for this list only.` : "Your confidence policy sets no floor. Type a number to filter this list only.",
            className: "h-8 w-28",
            disabled: value.confidenceReview,
            value: value.minConfidence === void 0 ? "" : Math.round(value.minConfidence * 100),
            onChange: (e) => {
              const raw = e.target.value;
              if (raw === "") onChange({ ...value, minConfidence: void 0 });
              else if (e.target.validity.valid) onChange({ ...value, minConfidence: Number(raw) / 100 });
            }
          }
        ),
        "%"
      ] }),
      /* @__PURE__ */ jsx(
        EnumSelect,
        {
          value: value.includeUnscored === void 0 ? "inherit" : String(value.includeUnscored),
          onChange: (v) => onChange({ ...value, includeUnscored: v === "inherit" ? void 0 : v === "true" }),
          options: [
            {
              value: "inherit",
              // Says what "inherited" resolves to: unscored rows are hidden the
              // moment any floor is set, which is the surprising half.
              label: `Unscored: inherited (${inherited.includeUnscored ? "included" : "hidden"})`
            },
            { value: "true", label: "Include unscored" },
            { value: "false", label: "Exclude unscored" }
          ],
          className: "h-8 w-48 text-sm"
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: value.confidenceReview ? "secondary" : "outline",
          "aria-pressed": Boolean(value.confidenceReview),
          onClick: () => onChange({ ...value, confidenceReview: value.confidenceReview ? void 0 : true }),
          children: value.confidenceReview ? "Review mode: all scores" : "Review all scores"
        }
      )
    ] }),
    active && /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        size: "sm",
        variant: "ghost",
        onClick: () => {
          setText("");
          reported.current = "";
          onChange({});
        },
        children: "Clear"
      }
    )
  ] });
}
function withFilters(base, filters) {
  const merged = { ...base };
  if (filters.minConfidence !== void 0) merged.minConfidence = filters.minConfidence;
  if (filters.includeUnscored !== void 0) merged.includeUnscored = filters.includeUnscored;
  if (filters.confidenceReview !== void 0) merged.confidenceReview = filters.confidenceReview;
  if (filters.search) merged.search = filters.search;
  if (filters.chain) merged.chain = filters.chain;
  if (filters.lifecycle) merged.lifecycle = filters.lifecycle;
  if (filters.groupType) merged.groupType = filters.groupType;
  if (filters.sagaKind) {
    merged.sagaKind = filters.sagaKind;
    merged.groupType = "transaction";
  }
  return Object.keys(merged).length > 0 ? merged : void 0;
}
var ALLOW_ALL = () => true;
function CatalogManager(props) {
  const { api, subscribe, ...rest } = props;
  const inner = /* @__PURE__ */ jsx(CatalogManagerInner, { ...rest });
  return api ? /* @__PURE__ */ jsx(CatalogProvider, { api, subscribe, children: inner }) : inner;
}
function CatalogManagerInner({
  kinds = [...CATALOG_ITEM_KINDS],
  defaultKind,
  query,
  includeTestnets,
  confirmDelete = true,
  showFilters = true,
  kindHints,
  primaryTarget,
  onOpenItem,
  canSetLifecycle,
  can = ALLOW_ALL,
  className
}) {
  const [selected, setSelected] = React2.useState(defaultKind ?? kinds[0]);
  const active = kinds.includes(selected) ? selected : defaultKind ?? kinds[0];
  const kindHint = kindHints?.[active];
  const [filters, setFilters] = React2.useState({});
  const effective = React2.useMemo(
    () => active === "group" ? filters : { ...filters, groupType: void 0, sagaKind: void 0 },
    [active, filters]
  );
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-4", className), children: [
    /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1 border-b", children: kinds.map((kind) => /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: () => setSelected(kind),
        className: cn(
          "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
          active === kind ? "border-brand text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
        ),
        children: pluralize(CATALOG_ITEM_KIND_LABEL[kind])
      },
      kind
    )) }),
    showFilters && /* @__PURE__ */ jsx(
      CatalogFilterBar,
      {
        value: effective,
        onChange: setFilters,
        subject: pluralize(CATALOG_ITEM_KIND_LABEL[active]).toLowerCase(),
        showGroupFacets: active === "group",
        includeTestnets
      }
    ),
    kindHint ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: kindHint }) : null,
    /* @__PURE__ */ jsx(
      CatalogKindPanel,
      {
        kind: active,
        query: withFilters(query, effective),
        filtered: Boolean(
          effective.minConfidence !== void 0 || effective.includeUnscored !== void 0 || effective.confidenceReview || effective.search || effective.chain || effective.lifecycle || effective.groupType || effective.sagaKind
        ),
        includeTestnets,
        confirmDelete,
        primaryTarget,
        onOpenItem,
        canSetLifecycle,
        canCreate: can(active, "create"),
        canEdit: can(active, "edit"),
        canDelete: can(active, "delete")
      },
      active
    )
  ] });
}
function CatalogKindPanel({
  kind,
  query,
  filtered = false,
  includeTestnets,
  confirmDelete,
  primaryTarget,
  onOpenItem,
  canSetLifecycle,
  canCreate,
  canEdit,
  canDelete
}) {
  const { api } = useCatalog();
  const advanced = useCatalogAdvanced();
  const items = useCatalogItems(kind, query);
  const [editRow, setEditRow] = React2.useState(null);
  const [customizeRow, setCustomizeRow] = React2.useState(null);
  const [duplicateRow, setDuplicateRow] = React2.useState(null);
  const [promoteRow, setPromoteRow] = React2.useState(null);
  const offeredRungs = useOfferedRungs();
  const rungOptions = React2.useMemo(
    () => ["tenant", "org", "customer"].filter(
      (r) => items.authorableRungs.includes(r) && (r === "tenant" || offeredRungs == null || offeredRungs.has(r))
    ),
    [items.authorableRungs, offeredRungs]
  );
  const shareTargets = rungOptions.filter((r) => r !== "tenant");
  const canPromote = canEdit && Boolean(api.promote) && shareTargets.length > 0;
  const canCustomize = canEdit && Boolean(api.getPresentation && api.setPresentation);
  const [pendingDelete, setPendingDelete] = React2.useState(null);
  const [actionError, setActionError] = React2.useState(null);
  const label = CATALOG_ITEM_KIND_LABEL[kind].toLowerCase();
  const impact = useAttachmentCount(pendingDelete?.id ?? null, kind);
  const runDelete = React2.useCallback(
    (row) => {
      setActionError(null);
      void items.remove(row.id).then(
        () => notifySuccess(`${CATALOG_ITEM_KIND_LABEL[kind]} deleted`, {
          description: rowName(row)
        })
      ).catch((err) => setActionError(err));
    },
    [items, kind]
  );
  const handleDelete = (row) => {
    if (confirmDelete) {
      setPendingDelete(row);
      return;
    }
    runDelete(row);
  };
  const listProps = {
    kind,
    items: items.items,
    isLoading: items.isLoading,
    error: items.error,
    filtered: filtered || items.confidencePolicy?.active,
    onRetry: items.refetch,
    // Omitted, not disabled: the list draws no action the caller cannot take.
    onEdit: canEdit ? setEditRow : void 0,
    onDelete: canDelete ? handleDelete : void 0,
    onCustomize: canCustomize ? setCustomizeRow : void 0,
    onPromote: canPromote ? setPromoteRow : void 0,
    // A duplicate IS a create: whoever may not create gets no copy button.
    onDuplicate: canCreate ? setDuplicateRow : void 0,
    onOpen: onOpenItem ? (row) => onOpenItem(kind, row) : void 0,
    resolveLogoUrl: api.resolveLogoUrl,
    hasMore: items.hasMore,
    onLoadMore: items.loadMore,
    isLoadingMore: items.isLoadingMore,
    total: items.total,
    totalCapped: items.totalCapped
  };
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("h3", { className: "text-sm font-semibold capitalize", children: pluralize(label) }),
        /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            size: "sm",
            variant: "outline",
            className: "h-7 text-xs",
            disabled: items.isLoading,
            onClick: items.refetch,
            children: items.isLoading ? "Refreshing\u2026" : `Refresh ${pluralize(label)}`
          }
        ),
        items.pendingCount > 0 && /* @__PURE__ */ jsxs(
          Button,
          {
            type: "button",
            size: "sm",
            variant: "outline",
            className: "h-7 text-xs",
            onClick: items.refetch,
            children: [
              items.pendingCount,
              " new ",
              pluralize(label, items.pendingCount),
              " \u2014 refresh"
            ]
          }
        )
      ] }),
      canCreate ? /* @__PURE__ */ jsx(
        CatalogItemDialog,
        {
          kind,
          mode: "create",
          includeTestnets,
          onSubmit: async (draft, { rung }) => {
            const row = await items.create(draft, { rung });
            notifySuccess(`${CATALOG_ITEM_KIND_LABEL[kind]} created`, {
              description: rowName(row)
            });
            return row;
          },
          rungOptions,
          trigger: /* @__PURE__ */ jsxs(Button, { type: "button", size: "sm", children: [
            "New ",
            label
          ] }),
          canSetLifecycle
        }
      ) : null
    ] }),
    actionError != null && /* @__PURE__ */ jsx(
      ErrorState,
      {
        error: actionError,
        size: "sm",
        title: `Could not delete the ${label}`,
        onRetry: () => setActionError(null),
        retryLabel: "Dismiss"
      }
    ),
    actionError == null && items.loadMoreError != null && /* @__PURE__ */ jsx(
      ErrorState,
      {
        error: items.loadMoreError,
        size: "sm",
        title: `Could not load more ${pluralize(label)}`,
        onRetry: items.loadMore
      }
    ),
    items.confidencePolicy?.active && /* @__PURE__ */ jsxs("p", { role: "status", className: "text-sm text-muted-foreground", children: [
      "Confidence filter active: at least ",
      Math.round(items.confidencePolicy.min_confidence * 100),
      "%.",
      items.confidencePolicy.include_unscored ? " Unscored findings included." : " Unscored findings hidden.",
      " Use Review all scores to inspect findings below your floor."
    ] }),
    primaryTarget && advanced ? /* @__PURE__ */ jsx(PrimaryAwareCatalogList, { target: primaryTarget, ...listProps }) : /* @__PURE__ */ jsx(CatalogList, { ...listProps }),
    editRow && canEdit && /* @__PURE__ */ jsx(
      CatalogItemDialog,
      {
        kind,
        mode: "edit",
        row: items.items.find((row) => row.id === editRow.id) ?? editRow,
        rowUnavailable: !items.items.some((row) => row.id === editRow.id),
        open: true,
        includeTestnets,
        onOpenChange: (open) => !open && setEditRow(null),
        onSubmit: async (draft) => {
          const row = await items.update(editRow.id, draft);
          notifySuccess(`${CATALOG_ITEM_KIND_LABEL[kind]} saved`, {
            description: rowName(row)
          });
          return row;
        },
        canSetLifecycle
      }
    ),
    customizeRow && canCustomize && /* @__PURE__ */ jsx(
      CatalogPresentationDialog,
      {
        kind,
        row: customizeRow,
        open: true,
        onOpenChange: (open) => !open && setCustomizeRow(null),
        onSaved: (result) => {
          if (result.row) items.replaceRow(result.row);
          else items.refetch();
          notifySuccess(
            result.row && deriveCatalogOwnership(result.row).customized ? `${CATALOG_ITEM_KIND_LABEL[kind]} customisation saved` : `${CATALOG_ITEM_KIND_LABEL[kind]} customisation reset`,
            { description: rowName(result.row ?? customizeRow) }
          );
        }
      }
    ),
    promoteRow && canPromote && /* @__PURE__ */ jsx(
      CatalogPromoteDialog,
      {
        kind,
        row: promoteRow,
        targets: shareTargets,
        open: true,
        onOpenChange: (open) => !open && setPromoteRow(null),
        onPromote: items.promote,
        onDone: (result) => notifySuccess(
          result.merged ? `${CATALOG_ITEM_KIND_LABEL[kind]} merged into the shared one` : `${CATALOG_ITEM_KIND_LABEL[kind]} shared with your ${result.targetRung}`,
          { description: rowName(result.row ?? promoteRow) }
        )
      }
    ),
    duplicateRow && canCreate && /* @__PURE__ */ jsx(
      CatalogItemDialog,
      {
        kind,
        mode: "create",
        open: true,
        initialDraft: duplicateDraft(kind, duplicateRow),
        title: `Duplicate ${label}`,
        description: deriveCatalogOwnership(duplicateRow).isSystemDefault ? `Creates a new ${label} in your workspace from \u201C${rowName(duplicateRow)}\u201D. It starts with nothing attached. To change how the platform ${label} itself reads here, use Customise instead: a copy attached to the same addresses shows up as a second chip.` : `Creates a new ${label} from \u201C${rowName(duplicateRow)}\u201D. It starts with nothing attached and needs its own name.`,
        includeTestnets,
        onOpenChange: (open) => !open && setDuplicateRow(null),
        onSubmit: async (draft) => {
          const row = await items.create(draft);
          notifySuccess(`${CATALOG_ITEM_KIND_LABEL[kind]} created`, {
            description: rowName(row)
          });
          return row;
        },
        canSetLifecycle
      }
    ),
    /* @__PURE__ */ jsx(
      AlertDialog,
      {
        open: pendingDelete != null,
        onOpenChange: (open) => !open && setPendingDelete(null),
        children: /* @__PURE__ */ jsxs(AlertDialogContent, { children: [
          /* @__PURE__ */ jsxs(AlertDialogHeader, { children: [
            /* @__PURE__ */ jsxs(AlertDialogTitle, { children: [
              "Delete ",
              label,
              " \u201C",
              rowName(pendingDelete),
              "\u201D?"
            ] }),
            /* @__PURE__ */ jsxs(AlertDialogDescription, { children: [
              "This removes the ",
              label,
              " from the catalog and detaches it from every address and transaction it was attached to. Attribution recorded through it \u2014 including who asserted it and why \u2014 goes with it."
            ] }),
            /* @__PURE__ */ jsx(
              "p",
              {
                className: cn(
                  "text-sm",
                  impact.tone === "some" || impact.tone === "unknown" ? "font-medium text-risk-critical" : "text-muted-foreground"
                ),
                children: impact.sentence
              }
            )
          ] }),
          /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [
            /* @__PURE__ */ jsx(AlertDialogCancel, { children: "Cancel" }),
            /* @__PURE__ */ jsxs(
              AlertDialogAction,
              {
                onClick: () => {
                  const row = pendingDelete;
                  setPendingDelete(null);
                  if (row) runDelete(row);
                },
                children: [
                  "Delete ",
                  label
                ]
              }
            )
          ] })
        ] })
      }
    )
  ] });
}
function useAttachmentCount(id, kind) {
  const { api } = useCatalog();
  const listAssignments = api.listAssignments;
  const [state, setState] = React2.useState({ total: null, totalCapped: false, loading: false, failed: false });
  React2.useEffect(() => {
    if (!id || !listAssignments) {
      setState({ total: null, totalCapped: false, loading: false, failed: false });
      return void 0;
    }
    let alive = true;
    setState({ total: null, totalCapped: false, loading: true, failed: false });
    listAssignments({ catalogKind: kind, catalogId: id, limit: 1 }).then((page) => {
      if (!alive) return;
      setState({
        // `?? null`, never `?? 0`: a page that carries no total has not told
        // us there is nothing attached.
        total: page.total ?? null,
        totalCapped: page.totalCapped === true,
        loading: false,
        failed: false
      });
    }).catch(() => {
      if (alive) setState({ total: null, totalCapped: false, loading: false, failed: true });
    });
    return () => {
      alive = false;
    };
  }, [id, kind, listAssignments]);
  return retirementImpact({ kind, ...state });
}
function rowName(row) {
  if (!row) return "";
  return typeof row.name === "string" && row.name !== "" ? row.name : row.id;
}
function PrimaryAwareCatalogList({
  target,
  ...listProps
}) {
  const primary = usePrimaryAssociations(target);
  const failure = describePrimaryFailure(primary.actionError);
  const kind = PRIMARY_KINDS.includes(listProps.kind) ? listProps.kind : null;
  const hiddenBy = kind ? primary.hiddenBy(kind) : void 0;
  const ownHide = kind ? primary.ownChoice(kind) : void 0;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    primary.actionError != null && /* @__PURE__ */ jsx(
      ErrorState,
      {
        error: primary.actionError,
        size: "sm",
        title: failure?.title ?? "Could not change the primary",
        ...failure ? { detail: failure.detail } : {},
        onRetry: primary.dismissActionError,
        retryLabel: "Dismiss",
        alwaysAllowRetry: true
      }
    ),
    kind && hiddenBy ? /* @__PURE__ */ jsxs("p", { role: "status", className: "flex flex-wrap items-center gap-2 text-sm text-muted-foreground", "data-testid": "primary-hidden", children: [
      /* @__PURE__ */ jsxs("span", { children: [
        "No primary ",
        kind,
        ":",
        " ",
        hiddenBy.rung === "user" ? "you chose none" : `${PRIMARY_RUNG_OWNER[hiddenBy.rung]} chose none`,
        "."
      ] }),
      ownHide && ownHide.catalogId === null ? /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", className: "h-6 px-2 text-xs", onClick: () => void primary.clearPrimary(kind), children: "Use inherited" }) : null
    ] }) : null,
    /* @__PURE__ */ jsx(CatalogList, { ...listProps, primary })
  ] });
}
var catalogMethodPolicy = {
  list: "read",
  getByIds: "read",
  listAssignments: "read",
  getChains: "read",
  getFieldOverrides: "read",
  getPresentation: "read",
  getPrimaries: "read",
  resolveLogoUrl: "read",
  // An uploaded asset is a draft until a row write persists its key.
  uploadLogo: "draft",
  create: "write",
  update: "write",
  remove: "write",
  setChains: "write",
  setFieldOverrides: "write",
  setPresentation: "write",
  promote: "write",
  setPrimary: "write",
  clearPrimary: "write"
};
var mutations = {
  create: (row, kind) => ({ resource: catalogResource(kind), id: row.id, operation: "created" }),
  update: (_row, kind, id) => ({ resource: catalogResource(kind), id, operation: "updated" }),
  remove: (_result, kind, id) => ({ resource: catalogResource(kind), id, operation: "deleted" }),
  setChains: (_result, kind, id) => ({ resource: catalogResource(kind), id, operation: "updated" }),
  setFieldOverrides: (_result, kind, id) => ({ resource: catalogResource(kind), id, operation: "updated" }),
  // Every workspace under the rung now reads this row differently everywhere it
  // appears — list, detail, and every chip on an address or transaction — so it
  // is an update of the row as far as any open view is concerned.
  setPresentation: (_result, kind, id) => ({ resource: catalogResource(kind), id, operation: "updated" }),
  // A moved row keeps answering at its old id (a one-hop redirect), so an open
  // view that re-reads the id it holds gets the row at its new rung.
  promote: (_result, kind, id) => ({ resource: catalogResource(kind), id, operation: "updated" }),
  setPrimary: (_result, target) => ({ resource: CATALOG_PRIMARY_RESOURCE, id: target.targetId, operation: "updated" }),
  clearPrimary: (_result, target) => ({ resource: CATALOG_PRIMARY_RESOURCE, id: target.targetId, operation: "updated" })
};
function withCatalogChanges(api, hub) {
  return withResourceMutations(api, hub, mutations);
}
function asArray(data) {
  if (Array.isArray(data)) return data;
  return data?.items ?? [];
}
function normalizeRow(row) {
  if (row == null || Array.isArray(row.chains)) return row;
  const appliesTo = row.applies_to_chains;
  return Array.isArray(appliesTo) ? { ...row, chains: appliesTo } : row;
}
var KIND_RESOURCE = {
  entity: "entities",
  tag: "tags",
  label: "labels",
  group: "groups",
  address: "addresses"
};
var LAYER_RUNGS = ["tenant", "org", "customer"];
var isLayerRung = (r) => typeof r === "string" && LAYER_RUNGS.includes(r);
function presentationFromWire(kind, id, body) {
  return {
    kind,
    catalogId: body?.catalog_id ?? id,
    rowRung: body?.row_rung ?? "global",
    base: body?.base ?? {},
    layers: (body?.layers ?? []).filter((layer) => isLayerRung(layer.rung)).map((layer) => ({
      rung: layer.rung,
      override: layer.override ?? null,
      inherited: layer.inherited ?? {},
      editable: layer.editable === true,
      updatedAt: layer.updated_at ?? null,
      updatedBy: layer.updated_by ?? null
    })),
    editableRungs: (body?.editable_rungs ?? []).filter(isLayerRung),
    row: body?.row ? normalizeRow(body.row) : void 0
  };
}
function createApiCatalogApi(client, options = {}) {
  const base = options.basePath ?? "/catalog";
  const resourceOf = (kind) => KIND_RESOURCE[kind] ?? kind;
  const kindPath = (kind) => `${base}/${resourceOf(kind)}`;
  const rowPath = (kind, id) => `${base}/${resourceOf(kind)}/${encodeURIComponent(id)}`;
  const assignmentsPath = `${base}-assignments`;
  const primaryPath = `${base}-primary`;
  const scopeOf = (target) => target.scope ?? "address";
  return {
    async list(kind, query = {}) {
      const res = await client.get(kindPath(kind), {
        // The hook owns the displayed page; an explicit refresh must reach the
        // server even when this identity did not originate the last mutation.
        cacheTtlMs: 0,
        query: {
          min_confidence: query.minConfidence,
          include_unscored: query.includeUnscored,
          confidence_review: query.confidenceReview,
          search: query.search,
          chain: query.chain,
          lifecycle: query.lifecycle,
          group_type: query.groupType,
          saga_kind: query.sagaKind,
          limit: query.limit,
          cursor: query.cursor
        }
      });
      const body = unwrapData(res);
      const envelope = Array.isArray(body) ? {} : body ?? {};
      return {
        items: asArray(body).map(normalizeRow),
        authorableRungs: envelope.authorable_rungs ?? void 0,
        nextCursor: envelope.next_cursor ?? void 0,
        hasMore: envelope.has_more ?? void 0,
        confidencePolicy: envelope.confidence_policy,
        total: envelope.approx_total ?? void 0,
        totalCapped: envelope.approx_total_capped ?? void 0
      };
    },
    async create(kind, body) {
      return normalizeRow(unwrapData(await client.post(kindPath(kind), body)));
    },
    async update(kind, id, body) {
      return normalizeRow(unwrapData(await client.put(rowPath(kind, id), body)));
    },
    async remove(kind, id) {
      unwrapData(await client.delete(rowPath(kind, id)));
    },
    async getByIds(kind, ids) {
      if (ids.length === 0) return [];
      const res = await client.get(kindPath(kind), {
        // Comma-JOINED, and not handed over as an array. `buildQueryString` repeats
        // the key per item for arrays, while this route reads a single
        // comma-separated `ids` (its swagger says so) — so a batch of three asked
        // `?ids=a&ids=b&ids=c` and the handler saw only `c`. Every chip but the last
        // then fell back to its raw id, which is what a reader saw on the page:
        // "Crypto" beside `label_c11a…df7b`. Safe to join because a catalog id is
        // `{kind}_{hex}` and cannot contain a comma.
        query: { ids: ids.join(",") }
      });
      return asArray(unwrapData(res)).map(normalizeRow);
    },
    // ── Reverse view (catalog row → resources) ────────────────────────────────
    async listAssignments(query) {
      const res = await client.get(assignmentsPath, {
        query: {
          catalog_kind: query.catalogKind,
          catalog_id: query.catalogId,
          chain: query.chain,
          target_scope: query.targetScope,
          limit: query.limit,
          cursor: query.cursor
        }
      });
      const data = unwrapData(res) ?? {};
      return {
        assignments: data.assignments ?? [],
        // The reverse lane answers a BOUNDED `approx_total`; `total` is the older
        // name, kept as a fallback so a lagging deploy still shows a count.
        total: data.approx_total ?? data.total ?? void 0,
        totalCapped: data.approx_total_capped ?? false,
        nextCursor: data.next_cursor ?? void 0,
        hasMore: data.has_more ?? false
      };
    },
    // ── Chain applicability ──────────────────────────────────────────────────
    async getChains(kind, id) {
      return unwrapData(await client.get(`${rowPath(kind, id)}/chains`));
    },
    async setChains(kind, id, chains) {
      unwrapData(await client.put(`${rowPath(kind, id)}/chains`, { chains }));
    },
    // ── Per-chain field overrides ────────────────────────────────────────────
    async getFieldOverrides(kind, id) {
      return unwrapData(
        await client.get(`${rowPath(kind, id)}/field-overrides`)
      );
    },
    async setFieldOverrides(kind, id, chainId, overrides) {
      unwrapData(
        await client.put(`${rowPath(kind, id)}/field-overrides/${encodeURIComponent(chainId)}`, {
          overrides
        })
      );
    },
    // ── Presentation of a row from a wider rung ──────────────────────────────
    async getPresentation(kind, id) {
      return presentationFromWire(
        kind,
        id,
        unwrapData(
          await client.get(`${rowPath(kind, id)}/presentation`, {
            // An editor must open on what is stored now, not on a cached answer
            // from before the last save.
            cacheTtlMs: 0
          })
        )
      );
    },
    async setPresentation(kind, id, rung, fields) {
      const path = `${rowPath(kind, id)}/presentation`;
      const res = fields == null ? await client.delete(path, { query: { rung } }) : await client.put(path, { rung, fields });
      return presentationFromWire(kind, id, unwrapData(res));
    },
    // ── Moving a row to a wider rung ─────────────────────────────────────────
    async promote(kind, id, toRung, cursor) {
      const body = unwrapData(
        await client.post(`${rowPath(kind, id)}/promote`, {
          to_rung: toRung,
          ...cursor ? { cursor } : {}
        })
      );
      return {
        sourceId: body?.source_id ?? id,
        targetId: body?.target_id ?? id,
        targetRung: body?.target_rung ?? toRung,
        merged: body?.merged === true,
        done: body?.done === true,
        cursor: body?.cursor ?? void 0,
        repointed: body?.repointed ?? {},
        row: body?.row ? normalizeRow(body.row) : void 0
      };
    },
    // ── Primary association ──────────────────────────────────────────────────
    //
    // snake_case, like every other call in this file. These three were the only
    // ones sending `targetId` / `chainId` / `catalogId`, and the service reads
    // `target_id` / `chain_id` / `catalog_id` — so even on the right path every
    // parameter arrived missing and `_require` rejected the request naming the
    // field the caller thought it had sent.
    //
    // The body is `{primaries, hidden, choices}`: the choice the caller sees per
    // kind WITH the rung that made it, the kinds a rung hid, and what every rung
    // on their ladder said — which is what lets a star say "set by your tenant"
    // and offer to undo only what is the caller's to undo.
    async getPrimaries(target) {
      const res = await client.get(primaryPath, {
        // A star just clicked elsewhere must be what the next read shows.
        cacheTtlMs: 0,
        query: {
          scope: scopeOf(target),
          target_id: target.targetId,
          chain_id: target.chainId ?? void 0
        }
      });
      return primaryStateFromWire(unwrapData(res));
    },
    async setPrimary(target, kind, catalogId, options2 = {}) {
      const body = unwrapData(
        await client.put(primaryPath, {
          scope: scopeOf(target),
          target_id: target.targetId,
          chain_id: target.chainId ?? void 0,
          kind,
          // `null` is sent, never dropped: it is "no primary at this rung", and
          // an absent `catalog_id` is a malformed request, not the same thing.
          catalog_id: catalogId,
          ...options2.rung ? { rung: options2.rung } : {},
          ...options2.expectedVersion != null ? { expected_version: options2.expectedVersion } : {}
        })
      );
      return primaryChoiceFromWire(body?.primary, kind);
    },
    async clearPrimary(target, kind, options2 = {}) {
      unwrapData(
        await client.delete(primaryPath, {
          query: {
            scope: scopeOf(target),
            target_id: target.targetId,
            chain_id: target.chainId ?? void 0,
            kind,
            rung: options2.rung
          }
        })
      );
    },
    // ── Logo (host-provided) ─────────────────────────────────────────────────
    ...options.uploadLogo ? { uploadLogo: options.uploadLogo } : {},
    ...options.resolveLogoUrl ? { resolveLogoUrl: options.resolveLogoUrl } : {}
  };
}
var isAbsolute = (logo) => /^(https?:)?\/\//i.test(logo) || logo.startsWith("data:") || logo.startsWith("blob:");
function provisionalId() {
  return `new-${randomHex(8)}`;
}
function createCatalogLogoAdapter(options) {
  const { client, cdnBase, validate, ticketPath } = options;
  return {
    async uploadLogo(input) {
      const result = await uploadLogo(
        client,
        { kind: input.kind, id: input.id ?? provisionalId(), chain: input.chain, file: input.file },
        { validate, path: ticketPath }
      );
      return {
        key: result.key,
        url: result.cdnUrl ?? (result.key ? safeAssetUrl(result.key, cdnBase) : void 0)
      };
    },
    resolveLogoUrl(logo) {
      if (!logo) return void 0;
      if (isAbsolute(logo)) return logo;
      return safeAssetUrl(logo, cdnBase);
    }
  };
}
function safeAssetUrl(key, cdnBase) {
  try {
    return assetUrl(key, { cdnBase });
  } catch {
    return void 0;
  }
}
var CATALOG_PACKAGE = "catalog";
createLogger(`fe:${CATALOG_PACKAGE}`);

export { CATALOG_PACKAGE, CatalogFilterBar, CatalogItemDialog, CatalogList, CatalogManager, CatalogPresentationDialog, CatalogPromoteDialog, CatalogProvider, PrimaryStar, SAGA_KIND_OPTIONS, applyCatalogChange, catalogChangeForRow, catalogMethodPolicy, countLabel, createApiCatalogApi, createCatalogLogoAdapter, describePrimaryFailure, isNarrowedQuery, mergeStreamedCatalogRow, shiftTotal, useAddresses, useCatalog, useCatalogItems, useEntities, useGroups, useLabels, useOptionalCatalog, usePrimaryAssociations, useTags, withCatalogChanges, withFilters, withPrimaryChoice };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map